import React, { FC, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  Business,
  CheckCircle,
  Cancel,
  MonetizationOn,
} from '@mui/icons-material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ExternalPerformanceViewProps } from '../performance.interfaces';
import { usePerformance } from '../performance.context';
import {
  getRatingColor,
  getStatusColor,
  getRatingLabel,
  getStatusLabel,
  formatScore,
  formatDate,
} from '../performance.helpers';
import { useTranslation } from 'src/hooks/useTranslationHook';
import PaginatedGrid from 'src/views/components/paginatedGrid/PaginatedGrid';

const PerformanceExternalView: FC<ExternalPerformanceViewProps> = ({
  reviews,
  summary,
  onReviewSelect,
}) => {
  const { fetchExternalReviews, state } = usePerformance();
  const { translateText } = useTranslation();

  useEffect(() => {
    if (reviews.length === 0) {
      fetchExternalReviews();
    }
  }, [reviews.length, fetchExternalReviews]);

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'partner',
      headerName: translateText(
        'analytics.performance.external.columns.partner'
      ),
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
            {params.row.name.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translateText('analytics.performance.external.id')}
              : {params.row.partnerId}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: translateText(
        'analytics.performance.external.columns.type'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{ textTransform: 'capitalize' }}
        >
          {params.row.partnerType.replace(/_/g, ' ')}
        </Typography>
      ),
    },
    {
      field: 'serviceDomain',
      headerName: translateText(
        'analytics.performance.external.columns.serviceDomain'
      ),
      width: 150,
    },
    {
      field: 'overallScore',
      headerName: translateText(
        'analytics.performance.external.columns.overallScore'
      ),
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight="medium">
          {formatScore(params.row.overallScore)}
        </Typography>
      ),
    },
    {
      field: 'rating',
      headerName: translateText(
        'analytics.performance.external.columns.rating'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getRatingLabel(
            params.row.overallRating,
            translateText
          )}
          size="small"
          sx={{
            backgroundColor: getRatingColor(params.row.overallRating),
            color: 'white',
            fontWeight: 'medium',
          }}
        />
      ),
    },
    {
      field: 'status',
      headerName: translateText(
        'analytics.performance.external.columns.status'
      ),
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getStatusLabel(params.row.status, translateText)}
          size="small"
          variant="outlined"
          sx={{
            borderColor: getStatusColor(params.row.status),
            color: getStatusColor(params.row.status),
          }}
        />
      ),
    },
    {
      field: 'contractRenewal',
      headerName: translateText(
        'analytics.performance.external.columns.contractRenewal'
      ),
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={
            params.row.contractRenewalRecommendation
              ? translateText(
                'analytics.performance.external.recommended'
              )
              : translateText(
                'analytics.performance.external.notRecommended'
              )
          }
          size="small"
          color={
            params.row.contractRenewalRecommendation
              ? 'success'
              : 'warning'
          }
          variant="outlined"
          icon={
            params.row.contractRenewalRecommendation ? (
              <CheckCircle />
            ) : (
              <Cancel />
            )
          }
        />
      ),
    },
    {
      field: 'reviewDate',
      headerName: translateText(
        'analytics.performance.external.columns.reviewDate'
      ),
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatDate(params.row.reviewDate)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: translateText(
        'analytics.performance.external.columns.actions'
      ),
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip
          title={translateText(
            'analytics.performance.external.viewDetails'
          )}
        >
          <IconButton
            size="small"
            onClick={() => onReviewSelect(params.row)}
            color="primary"
            sx={{
              minHeight: { xs: '44px', sm: 'auto' },
              minWidth: { xs: '44px', sm: 'auto' },
            }}
          >
            <Visibility />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [translateText, onReviewSelect]);

  if (state.isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {translateText('analytics.performance.external.loading')}
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  const renewalRecommended = reviews.filter(
    (r) => r.contractRenewalRecommendation
  ).length;
  const renewalNotRecommended = reviews.filter(
    (r) => !r.contractRenewalRecommendation
  ).length;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }} data-testid="performance-external-view">
      {/* Summary Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 4 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Business color="primary" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.external.totalReviews')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {summary.totalReviews}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText(
                  'analytics.performance.external.externalPartnerReviews'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MonetizationOn color="success" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="success.main"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.external.avgRating')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {summary.averageRating.toFixed(1)}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText('analytics.performance.external.outOf10')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircle color="success" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="success.main"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.external.renewalRec')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {renewalRecommended}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText(
                  'analytics.performance.external.recommendedForRenewal'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Cancel color="warning" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="warning.main"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.external.notRenewed')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {renewalNotRecommended}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText('analytics.performance.external.notRecommended')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Distribution Chart */}
      <Card elevation={2} sx={{ mb: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {translateText(
              'analytics.performance.external.performanceDistribution'
            )}
          </Typography>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {Object.entries(summary.ratingDistribution).map(
              ([rating, count]) => (
                <Grid item xs={6} sm={4} md={2.4} key={rating}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      sx={{
                        color: getRatingColor(rating as any),
                        fontSize: { xs: '1.5rem', sm: '2.125rem' },
                      }}
                    >
                      {count}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        textTransform: 'capitalize',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      {rating.replace(/([A-Z])/g, ' $1').trim()}
                    </Typography>
                  </Box>
                </Grid>
              )
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* External Partner Reviews Table */}
      <Card elevation={2}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {translateText(
              'analytics.performance.external.externalPartnerPerformanceReviews'
            )}
          </Typography>
          {reviews.length === 0 && !state.isLoading ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
              <Business sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                {translateText('analytics.performance.external.noReviewsFound')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText(
                  'analytics.performance.external.reviewsWillAppear'
                )}
              </Typography>
            </Box>
          ) : (
            <PaginatedGrid
              dataTestId="performance-external-grid"
              columns={columns}
              rows={reviews}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PerformanceExternalView;
